import { useEffect, useRef } from "react";

/**
 * Sincroniza el estado del usuario con la cookie periódicamente
 * @param {Object} params
 * @param {Function} params.getUserFromCookies - función que devuelve el usuario desde cookies
 * @param {Function} params.fetchCurrentUser - función async que actualiza datos del usuario desde backend
 * @param {Function} params.deleteCookieDynamic - función para eliminar cookie por nombre
 * @param {Function} params.setUserAutenticate - setter de estado del usuario
 * @param {number} [params.intervalMs=2000] - intervalo en ms
 */
export default function useUserCookieSync({
  getUserFromCookies,
  fetchCurrentUser,
  deleteCookieDynamic,
  setUserAutenticate,
  intervalMs = 2000,
}) {
  // Guardar la última firma comparada para evitar refetches por cambios de forma
  const lastSignatureRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      try {
        const cookie = getUserFromCookies();
        if (!cookie) return;

        // Firma estable: identidad + tokens
        const identity = cookie.id || cookie.email || cookie.username || "";
        const access = cookie.access || cookie.token || "";
        const refresh = cookie.refresh || "";
        const signature = `${identity}|${access}|${refresh}`;

        if (signature === lastSignatureRef.current) {
          return; // nada cambió de forma relevante
        }

        // Actualizar ref primero para evitar ráfagas en strict mode
        lastSignatureRef.current = signature;

        // Obtener versión normalizada desde backend y setear una sola vez
        fetchCurrentUser(cookie)
          .then((updatedUser) => {
            if (!updatedUser) return;
            setUserAutenticate((prev) => {
              try {
                const a = JSON.stringify(prev);
                const b = JSON.stringify(updatedUser);
                return a === b ? prev : updatedUser;
              } catch (_) {
                return updatedUser;
              }
            });
          })
          .catch(() => { /* silencio: próximo tick reintenta si cambia firma */ });
      } catch (error) {
        console.warn("Error parsing cookie during sync:", error);
        // Limpiar cookies corruptas (dinámico)
        deleteCookieDynamic("autenticator_user");
        setUserAutenticate(null);
      }
    }, intervalMs);
    return () => clearInterval(interval);
  }, [getUserFromCookies, fetchCurrentUser, deleteCookieDynamic, setUserAutenticate, intervalMs]);
}
